/**
 * Domain-Specific Document Requirements
 *
 * Different industries have additional document requirements
 * beyond the base checklist.
 */

import type { DocumentCategory } from './types';

export type StartupDomain =
  | 'saas'
  | 'fintech'
  | 'healthcare'
  | 'edtech'
  | 'marketplace'
  | 'ai-ml'
  | 'hardware'
  | 'biotech'
  | 'consumer'
  | 'enterprise'
  | 'other';

export const DOMAIN_LABELS: Record<StartupDomain, string> = {
  saas: 'SaaS / Software',
  fintech: 'Fintech / Financial Services',
  healthcare: 'Healthcare / Life Sciences',
  edtech: 'EdTech',
  marketplace: 'Marketplace / E-commerce',
  'ai-ml': 'AI / Machine Learning',
  hardware: 'Hardware / DeepTech',
  biotech: 'Biotech / Pharma',
  consumer: 'Consumer',
  enterprise: 'Enterprise',
  other: 'Other',
};

export interface DomainDocument {
  id: string;
  name: string;
  description: string;
  category: DocumentCategory;
  required: boolean;
}

// Domain-specific additional documents
export const DOMAIN_DOCUMENTS: Record<StartupDomain, DomainDocument[]> = {
  saas: [
    {
      id: 'saas-1',
      name: 'Master Service Agreements',
      description: 'Enterprise customer contract templates',
      category: 'commercial',
      required: true,
    },
    {
      id: 'saas-2',
      name: 'Data Processing Agreements',
      description: 'DPA templates for GDPR compliance',
      category: 'compliance',
      required: true,
    },
    {
      id: 'saas-3',
      name: 'SLA Documentation',
      description: 'Uptime and support guarantees',
      category: 'commercial',
      required: true,
    },
    {
      id: 'saas-4',
      name: 'Enterprise Security Questionnaires',
      description: 'Completed customer security reviews',
      category: 'technology',
      required: false,
    },
    {
      id: 'saas-5',
      name: 'Subprocessor List',
      description: 'Third-party data processors',
      category: 'compliance',
      required: true,
    },
  ],
  fintech: [
    {
      id: 'fin-dom-1',
      name: 'Money Transmitter Licenses',
      description: 'State-by-state licensing documentation',
      category: 'compliance',
      required: true,
    },
    {
      id: 'fin-dom-2',
      name: 'Banking Partnerships',
      description: 'Sponsor bank agreements',
      category: 'contracts',
      required: true,
    },
    {
      id: 'fin-dom-3',
      name: 'AML/KYC Policies',
      description: 'Anti-money laundering compliance',
      category: 'compliance',
      required: true,
    },
    {
      id: 'fin-dom-4',
      name: 'BSA Compliance',
      description: 'Bank Secrecy Act documentation',
      category: 'compliance',
      required: true,
    },
    {
      id: 'fin-dom-5',
      name: 'PCI-DSS Certification',
      description: 'Payment card security compliance',
      category: 'technology',
      required: true,
    },
    {
      id: 'fin-dom-6',
      name: 'Regulatory Examination Reports',
      description: 'OCC, FDIC, state regulator reports',
      category: 'compliance',
      required: false,
    },
  ],
  healthcare: [
    {
      id: 'health-1',
      name: 'FDA Submissions & Approvals',
      description: '510(k), PMA, IND, NDA documentation',
      category: 'compliance',
      required: true,
    },
    {
      id: 'health-2',
      name: 'Clinical Trial Data',
      description: 'Protocols, results, patient data',
      category: 'commercial',
      required: true,
    },
    {
      id: 'health-3',
      name: 'IRB Approvals',
      description: 'Institutional Review Board approvals',
      category: 'compliance',
      required: true,
    },
    {
      id: 'health-4',
      name: 'HIPAA Compliance',
      description: 'Privacy and security policies',
      category: 'compliance',
      required: true,
    },
    {
      id: 'health-5',
      name: 'Medical Device Certifications',
      description: 'CE marking, UL certifications',
      category: 'compliance',
      required: false,
    },
    {
      id: 'health-6',
      name: 'Quality Management System',
      description: 'ISO 13485 certification',
      category: 'technology',
      required: false,
    },
  ],
  edtech: [
    {
      id: 'edu-1',
      name: 'FERPA Compliance',
      description: 'Student data privacy policies',
      category: 'compliance',
      required: true,
    },
    {
      id: 'edu-2',
      name: 'COPPA Compliance',
      description: "Children's privacy (under-13)",
      category: 'compliance',
      required: true,
    },
    {
      id: 'edu-3',
      name: 'Accessibility Compliance',
      description: 'WCAG 2.1, Section 508',
      category: 'technology',
      required: true,
    },
    {
      id: 'edu-4',
      name: 'Student Data Agreements',
      description: 'District/school data sharing',
      category: 'contracts',
      required: true,
    },
    {
      id: 'edu-5',
      name: 'Accreditation Documentation',
      description: 'If offering credentials',
      category: 'compliance',
      required: false,
    },
  ],
  marketplace: [
    {
      id: 'mkt-1',
      name: 'Seller/Merchant Agreements',
      description: 'Platform terms for sellers',
      category: 'contracts',
      required: true,
    },
    {
      id: 'mkt-2',
      name: 'Payment Processing Agreements',
      description: 'Stripe, PayPal, etc.',
      category: 'contracts',
      required: true,
    },
    {
      id: 'mkt-3',
      name: 'Escrow Arrangements',
      description: 'If holding funds',
      category: 'contracts',
      required: false,
    },
    {
      id: 'mkt-4',
      name: 'Consumer Protection Compliance',
      description: 'FTC guidelines',
      category: 'compliance',
      required: true,
    },
    {
      id: 'mkt-5',
      name: 'Product Liability Insurance',
      description: 'Coverage for marketplace goods',
      category: 'contracts',
      required: false,
    },
  ],
  'ai-ml': [
    {
      id: 'ai-1',
      name: 'Training Data Provenance',
      description: 'Source and licensing of training data',
      category: 'ip',
      required: true,
    },
    {
      id: 'ai-2',
      name: 'Model Documentation',
      description: 'Architecture, training, evaluation',
      category: 'technology',
      required: true,
    },
    {
      id: 'ai-3',
      name: 'Bias/Fairness Audits',
      description: 'Algorithmic bias assessment',
      category: 'technology',
      required: false,
    },
    {
      id: 'ai-4',
      name: 'AI Ethics Policies',
      description: 'Responsible AI guidelines',
      category: 'esg-governance',
      required: false,
    },
    {
      id: 'ai-5',
      name: 'Open Source AI Licenses',
      description: 'Model and framework licensing',
      category: 'ip',
      required: true,
    },
    {
      id: 'ai-6',
      name: 'AI Regulatory Compliance',
      description: 'EU AI Act, sector-specific',
      category: 'compliance',
      required: false,
    },
  ],
  hardware: [
    {
      id: 'hw-1',
      name: 'Bill of Materials',
      description: 'Component specifications',
      category: 'technology',
      required: true,
    },
    {
      id: 'hw-2',
      name: 'Manufacturing Agreements',
      description: 'CM/ODM contracts',
      category: 'contracts',
      required: true,
    },
    {
      id: 'hw-3',
      name: 'Supply Chain Documentation',
      description: 'Supplier agreements',
      category: 'contracts',
      required: true,
    },
    {
      id: 'hw-4',
      name: 'Certification/Testing Reports',
      description: 'FCC, CE, UL certifications',
      category: 'compliance',
      required: true,
    },
    {
      id: 'hw-5',
      name: 'Environmental Compliance',
      description: 'RoHS, WEEE documentation',
      category: 'compliance',
      required: false,
    },
    {
      id: 'hw-6',
      name: 'Tooling/Mold Ownership',
      description: 'Manufacturing tooling rights',
      category: 'ip',
      required: false,
    },
  ],
  biotech: [
    {
      id: 'bio-1',
      name: 'FDA/EMA Submissions',
      description: 'Regulatory submissions and status',
      category: 'compliance',
      required: true,
    },
    {
      id: 'bio-2',
      name: 'Clinical Trial Phase Data',
      description: 'Phase I/II/III results',
      category: 'commercial',
      required: true,
    },
    {
      id: 'bio-3',
      name: 'GxP Documentation',
      description: 'GMP, GLP, GCP compliance',
      category: 'compliance',
      required: true,
    },
    {
      id: 'bio-4',
      name: 'CMO/CDMO Contracts',
      description: 'Manufacturing agreements',
      category: 'contracts',
      required: true,
    },
    {
      id: 'bio-5',
      name: 'Patent Portfolio',
      description: 'Compound and method patents',
      category: 'ip',
      required: true,
    },
  ],
  consumer: [],
  enterprise: [],
  other: [],
};

/**
 * Get domain-specific documents
 */
export function getDomainDocuments(domain: StartupDomain): DomainDocument[] {
  return DOMAIN_DOCUMENTS[domain] || [];
}

/**
 * Get required domain documents
 */
export function getRequiredDomainDocuments(domain: StartupDomain): DomainDocument[] {
  return getDomainDocuments(domain).filter((doc) => doc.required);
}

/**
 * Get optional domain documents
 */
export function getOptionalDomainDocuments(domain: StartupDomain): DomainDocument[] {
  return getDomainDocuments(domain).filter((doc) => !doc.required);
}
